import { useState } from "react";

import { changeRAndDUpperCase } from "@frontend/common";

import {
  FundingTargetPressData,
  TargetPressData,
} from "@api/dealsSimilarData/dealsSimilarData.type";
import { formatDate } from "@utils/formatDate";

import * as S from "./TargetPressTableContainer.style";

const getClassificationDisplayName = (classification: string | undefined) => {
  if (["C801", "C802"].includes(classification || "")) return "Filing";
  if (["C006", "C021"].includes(classification || "")) return "Press";
  if (classification === "") return "-";
  return classification;
};

const TargetPressTableContainer = ({
  data,
  similarDataType,
}: {
  data: TargetPressData | FundingTargetPressData;
  similarDataType: "deal" | "funding";
}) => {
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  if (similarDataType === "funding" && "roundType" in data) {
    return (
      <S.TargetPressTableContainer>
        <S.TargetPressTableTitle>Current Press</S.TargetPressTableTitle>
        <S.TargetPressTableWrapper>
          <S.TargetPressTableHeader>
            <S.TargetPressTablePressId>
              <S.TargetPressTableHeadDiv>Press ID</S.TargetPressTableHeadDiv>
            </S.TargetPressTablePressId>
            <S.TargetPressTablePressDate>
              <S.TargetPressTableHeadDiv>Press Date</S.TargetPressTableHeadDiv>
            </S.TargetPressTablePressDate>
            <S.TargetPressTableInvestee>
              <S.TargetPressTableHeadDiv>Investee</S.TargetPressTableHeadDiv>
            </S.TargetPressTableInvestee>
            <S.TargetPressTableInvestor>
              <S.TargetPressTableHeadDiv>Investor</S.TargetPressTableHeadDiv>
            </S.TargetPressTableInvestor>
            <S.TargetPressTableClassification>
              <S.TargetPressTableHeadDiv>
                Classification
              </S.TargetPressTableHeadDiv>
            </S.TargetPressTableClassification>
            <S.TargetPressTableClassification>
              <S.TargetPressTableHeadDiv>Round Type</S.TargetPressTableHeadDiv>
            </S.TargetPressTableClassification>
            <S.TargetPressTableClassification>
              <S.TargetPressTableHeadDiv>Amount</S.TargetPressTableHeadDiv>
            </S.TargetPressTableClassification>
            <S.TargetPressTablePressTitle>
              <S.TargetPressTableHeadDiv>Press Title</S.TargetPressTableHeadDiv>
            </S.TargetPressTablePressTitle>
          </S.TargetPressTableHeader>

          <S.TargetPressTableBody
            key={data?.pressId}
            onMouseEnter={() => setHoveredRow(data?.pressId)}
            onMouseLeave={() => setHoveredRow(null)}
            hovered={hoveredRow === data?.pressId}
          >
            <S.TargetPressTablePressId>
              <span> #{data?.pressId}</span>
            </S.TargetPressTablePressId>

            <S.TargetPressTablePressDateData>
              <S.TargetPressTableBodyDiv>
                <div> {formatDate(data?.pressDate[0]?.text, "YYYY.MM.DD")}</div>
                <div>{data?.pressDate[0].isManual ? "(Manual)" : ""}</div>
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTablePressDateData>

            <S.TargetPressTableInvesteeData>
              <S.TargetPressTableBodyDiv>
                {data?.investee.map((investee) => (
                  <div>{investee.text}</div>
                ))}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableInvesteeData>

            <S.TargetPressTableInvestorData>
              <S.TargetPressTableBodyDiv>
                {data?.investor.map((investor) => (
                  <div>{investor.text}</div>
                ))}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableInvestorData>

            <S.TargetPressTableClassificationData>
              <S.TargetPressTableBodyDiv>
                {getClassificationDisplayName(data?.classification)}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableClassificationData>

            <S.TargetPressTableClassificationData>
              <S.TargetPressTableBodyDiv>
                {data?.roundType.map((roundType) => (
                  <>{changeRAndDUpperCase(roundType.text)}</>
                ))}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableClassificationData>
            <S.TargetPressTableClassificationData>
              <S.TargetPressTableBodyDiv>
                <>{data?.amount?.text}</>
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableClassificationData>

            <S.TargetPressTablePressTitleData>
              <S.TargetPressTablePressTitleOverflow>
                {data?.pressTitle}
              </S.TargetPressTablePressTitleOverflow>
            </S.TargetPressTablePressTitleData>
          </S.TargetPressTableBody>
        </S.TargetPressTableWrapper>
      </S.TargetPressTableContainer>
    );
  }

  if (similarDataType === "deal" && "dealType" in data) {
    return (
      <S.TargetPressTableContainer>
        <S.TargetPressTableTitle>Current Press</S.TargetPressTableTitle>
        <S.TargetPressTableWrapper>
          <S.TargetPressTableHeader>
            <S.TargetPressTablePressId>
              <S.TargetPressTableHeadDiv>Press ID</S.TargetPressTableHeadDiv>
            </S.TargetPressTablePressId>
            <S.TargetPressTablePressDate>
              <S.TargetPressTableHeadDiv>Press Date</S.TargetPressTableHeadDiv>
            </S.TargetPressTablePressDate>
            <S.TargetPressTableSeller>
              <S.TargetPressTableHeadDiv>Seller</S.TargetPressTableHeadDiv>
            </S.TargetPressTableSeller>
            <S.TargetPressTableBuyer>
              <S.TargetPressTableHeadDiv>Buyer</S.TargetPressTableHeadDiv>
            </S.TargetPressTableBuyer>
            <S.TargetPressTableClassification>
              <S.TargetPressTableHeadDiv>
                Classification
              </S.TargetPressTableHeadDiv>
            </S.TargetPressTableClassification>
            <S.TargetPressTableDealType>
              <S.TargetPressTableHeadDiv>Deal Type</S.TargetPressTableHeadDiv>
            </S.TargetPressTableDealType>
            <S.TargetPressTablePressTitle>
              <S.TargetPressTableHeadDiv>
                {" "}
                Press Title
              </S.TargetPressTableHeadDiv>
            </S.TargetPressTablePressTitle>
          </S.TargetPressTableHeader>

          <S.TargetPressTableBody
            key={data?.pressId}
            onMouseEnter={() => setHoveredRow(data?.pressId)}
            onMouseLeave={() => setHoveredRow(null)}
            hovered={hoveredRow === data?.pressId}
          >
            <S.TargetPressTablePressId>
              <span> #{data?.pressId}</span>
            </S.TargetPressTablePressId>

            <S.TargetPressTablePressDateData>
              <S.TargetPressTableBodyDiv>
                <div> {formatDate(data?.pressDate[0]?.text, "YYYY.MM.DD")}</div>
                <div>{data?.pressDate[0].isManual ? "(Manual)" : ""}</div>
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTablePressDateData>

            <S.TargetPressTableSellerData>
              <S.TargetPressTableBodyDiv>
                {data?.seller.map((seller) => (
                  <div>{seller.text}</div>
                ))}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableSellerData>

            <S.TargetPressTableBuyerData>
              <S.TargetPressTableBodyDiv>
                {data?.buyer.map((buyer) => (
                  <div>{buyer.text}</div>
                ))}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableBuyerData>

            <S.TargetPressTableClassificationData>
              <S.TargetPressTableBodyDiv>
                {getClassificationDisplayName(data?.classification)}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableClassificationData>

            <S.TargetPressTableDealTypeData>
              <S.TargetPressTableBodyDiv>
                {data?.dealType.map((dealType) => (
                  <div>{changeRAndDUpperCase(dealType.text)}</div>
                ))}
              </S.TargetPressTableBodyDiv>
            </S.TargetPressTableDealTypeData>

            <S.TargetPressTablePressTitleData>
              <S.TargetPressTablePressTitleOverflow>
                {data?.pressTitle}
              </S.TargetPressTablePressTitleOverflow>
            </S.TargetPressTablePressTitleData>
          </S.TargetPressTableBody>
        </S.TargetPressTableWrapper>
      </S.TargetPressTableContainer>
    );
  }

  return null;
};

export default TargetPressTableContainer;
